import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { Order, PublicEvent, PublicTicketProduct } from '@gatecheckpro/contracts';
import {
  Button,
  EmptyState,
  LoadingRegion,
  Notice,
  SALE_STATES,
  Status,
  formatDateInZone,
  formatMoneyOrFree,
  formatRangeInZone,
  presentation,
} from '@gatecheckpro/design-system';
import { ApiRefusal } from '@gatecheckpro/api-client';
import { Chrome } from './Chrome.tsx';
import { useResource } from '../lib/useResource.ts';
import { useCommand } from '../lib/useCommand.ts';
import { FailureNotice } from '../lib/refusal.tsx';
import { useSession } from '../lib/session.tsx';

/**
 * A public event page.
 *
 * Everything here comes from the public projection, which carries no gate
 * configuration, no zone mapping and no inventory. The page could not leak them
 * if it tried — they are not in the response.
 *
 * The purchase action is the thing to get right. Checkout does not exist, so
 * there is no button. A disabled "Buy" would be worse: it implies the feature is
 * there and something is wrong.
 */
export function EventPage() {
  const { slug } = useParams<{ slug: string }>();
  const { resource } = useResource<PublicEvent>(
    slug === undefined ? null : `/api/v1/public/events/${slug}`,
  );

  if (resource.status === 'loading') {
    return (
      <Chrome>
        <main className="gc-main">
          <div className="gc-container">
            <LoadingRegion label="Loading event" />
          </div>
        </main>
      </Chrome>
    );
  }

  if (resource.status === 'failed') {
    const notFound = resource.error instanceof ApiRefusal && resource.error.status === 404;
    return (
      <Chrome>
        <main className="gc-main">
          <div className="gc-container gc-stack">
            {notFound ? (
              <EmptyState
                title="No such event"
                action={
                  <Link to="/" className="gc-button gc-button--primary">
                    See what&rsquo;s on
                  </Link>
                }
              >
                This link does not point at anything. It may never have, or the organizer may have
                taken the event down.
              </EmptyState>
            ) : (
              <FailureNotice error={resource.error} />
            )}
          </div>
        </main>
      </Chrome>
    );
  }

  const event = resource.data;

  return (
    <Chrome>
      <section className="gc-event-hero">
        <div className="gc-container gc-stack">
          <span className="gc-eyebrow">
            <Link to="/">What&rsquo;s on</Link>
          </span>
          <h1 className="gc-event-hero__title">{event.title}</h1>
          {event.publicSummary !== null && <p className="gc-lede">{event.publicSummary}</p>}
          {event.venue !== null && (
            <p className="gc-muted">
              {event.venue.name} · {event.venue.locality}
              {event.venue.addressLine !== null && ` · ${event.venue.addressLine}`}
            </p>
          )}
        </div>
      </section>

      <main className="gc-main">
        <div className="gc-container gc-stack gc-stack--loose" style={{ maxWidth: '56rem' }}>
          <section className="gc-stack">
            <h2>When</h2>
            {event.sessions.length === 0 ? (
              <p className="gc-muted">Dates to be announced.</p>
            ) : (
              <div className="gc-stack gc-stack--tight">
                {event.sessions.map((session) => (
                  <div key={session.sessionId} className="gc-card gc-stack gc-stack--tight">
                    {session.name !== null && <strong>{session.name}</strong>}
                    <span>{formatDateInZone(session.startsAt, event.timezone)}</span>
                    <span className="gc-muted">
                      {formatRangeInZone(session.startsAt, session.endsAt, event.timezone)}
                      {session.doorsOpenAt !== null && ' · doors earlier'}
                    </span>
                    {/*
                      Offered for every date, because whether a night sells
                      reserved seats is the server's answer and the public event
                      projection does not carry it. The room page says plainly
                      when a date has no seating rather than this page guessing.
                    */}
                    <Link to={`/e/${event.slug}/sessions/${session.sessionId}/seats`}>
                      Choose seats for this date
                    </Link>
                  </div>
                ))}
                <p className="gc-muted">All times are local to the venue ({event.timezone}).</p>
              </div>
            )}
          </section>

          {event.publicDescription !== null && (
            <section className="gc-stack">
              <h2>About</h2>
              <p style={{ maxWidth: 'var(--gc-layout-reading)', whiteSpace: 'pre-wrap' }}>
                {event.publicDescription}
              </p>
            </section>
          )}

          <section className="gc-stack">
            <h2>Tickets</h2>

            {event.ticketProducts.length === 0 ? (
              <EmptyState title="No tickets listed yet">
                The organizer has not published any ticket types for this event.
              </EmptyState>
            ) : (
              <div className="gc-grid">
                {event.ticketProducts.map((product) => (
                  <TicketCard key={product.ticketProductId} product={product} slug={event.slug} />
                ))}
              </div>
            )}

            {/*
              What a customer can actually do here, in the server's words. The
              two states are genuinely different — "reserve then pay" and
              "reserve, but this deployment cannot charge" — and neither is
              inferred by this page.
            */}
            <Notice
              tone={event.commerce.state === 'AVAILABLE' ? 'info' : 'caution'}
              title={
                event.commerce.state === 'AVAILABLE'
                  ? 'How buying works'
                  : 'You can reserve, but not yet pay'
              }
            >
              {event.commerce.message}
            </Notice>
          </section>
        </div>
      </main>
    </Chrome>
  );
}

/**
 * One ticket type, and the reservation flow that starts from it.
 *
 * The action says **Reserve**, not Buy, because reserving is exactly what it
 * does. Nothing is charged, and the card says so before the customer commits
 * rather than after (kickoff Section 17).
 */
function TicketCard({
  product,
  slug,
}: {
  readonly product: PublicTicketProduct;
  readonly slug: string;
}) {
  const { state } = useSession();
  const navigate = useNavigate();
  const command = useCommand<Order>();
  const [quantity, setQuantity] = useState(1);
  /**
   * Identifies this intended purchase, not this request.
   *
   * Fixed for as long as the card is mounted, so a retry after a dropped
   * response replays rather than reserving twice — and different for a customer
   * who comes back later meaning to buy more.
   */
  const [attempt] = useState(() => crypto.randomUUID());
  const sale = presentation(SALE_STATES, product.saleState);

  async function reserve(): Promise<void> {
    const order = await command.run('/api/v1/customer/checkout', {
      method: 'POST',
      body: { ticketProductId: product.ticketProductId, quantity },
      // A dropped response on a congested network must not take the tickets
      // twice, so this is stable for as long as the card is mounted.
      //
      // Keyed per *intended purchase*, not per product-and-quantity.
      //
      // The old key was the same string for every customer buying the same
      // ticket, which meant the second buyer replayed the first buyer's order.
      // The server now refuses a cross-actor replay outright, but a client
      // should not be generating colliding keys in the first place — and a
      // customer deliberately buying two more tickets later needs a new order,
      // not their old one handed back.
      idempotencyKey: `checkout:${product.ticketProductId}:${String(quantity)}:${attempt}`,
    });
    if (order !== null) void navigate(`/orders/${order.orderId}`);
  }

  return (
    <article className="gc-ticket-card">
      <div className="gc-row gc-row--between">
        <strong>{product.displayName}</strong>
        <Status tone={sale.tone} glyph={sale.glyph} label={sale.label} title={sale.meaning} />
      </div>

      {product.description !== null && <p className="gc-muted">{product.description}</p>}

      <span className="gc-ticket-card__price">{formatMoneyOrFree(product.price)}</span>

      {product.accessScope === 'SPECIFIC_SESSIONS' && (
        <span className="gc-muted">Valid for {product.sessionIds.length} of the dates above.</span>
      )}

      <FailureNotice error={command.error} />

      {state.status === 'authenticated' ? (
        <div className="gc-stack gc-stack--tight">
          <div className="gc-row gc-row--between">
            <label className="gc-field__label" htmlFor={`quantity-${product.ticketProductId}`}>
              How many?
            </label>
            <div className="gc-quantity">
              <button
                type="button"
                className="gc-quantity__button"
                aria-label="One fewer"
                disabled={quantity <= 1 || command.busy}
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
              >
                −
              </button>
              <output
                className="gc-quantity__value"
                id={`quantity-${product.ticketProductId}`}
                aria-live="polite"
              >
                {quantity}
              </output>
              <button
                type="button"
                className="gc-quantity__button"
                aria-label="One more"
                disabled={quantity >= 10 || command.busy}
                onClick={() => setQuantity((current) => Math.min(10, current + 1))}
              >
                +
              </button>
            </div>
          </div>

          <Button
            variant="primary"
            size="large"
            block
            busy={command.busy}
            onClick={() => void reserve()}
          >
            Reserve {quantity === 1 ? 'a ticket' : `${String(quantity)} tickets`}
          </Button>
          <span className="gc-muted">
            Holds them for ten minutes. Nothing is charged — payment is not live yet.
          </span>
        </div>
      ) : (
        <div className="gc-stack gc-stack--tight">
          <Link
            to={`/sign-in?next=${encodeURIComponent(`/e/${slug}`)}`}
            className="gc-button gc-button--primary gc-button--block"
          >
            Sign in to reserve
          </Link>
          <span className="gc-muted">
            A ticket has to belong to somebody, so reserving needs an account.
          </span>
        </div>
      )}
    </article>
  );
}
